<script setup lang="ts">
import Icon from '@/components/icons/Icon.vue'

withDefaults(
  defineProps<{
    variant?: 'info' | 'success' | 'warning' | 'error'
    title?: string
  }>(),
  { variant: 'info' }
)

const iconMap: Record<string, 'info' | 'exclamation' | 'check'> = {
  info: 'info',
  success: 'check',
  warning: 'exclamation',
  error: 'exclamation',
}
</script>

<template>
  <div class="alert" :class="`alert--${variant}`" role="alert">
    <div class="alert__icon">
      <Icon :name="iconMap[variant]" :size="20" />
    </div>
    <div class="alert__body">
      <strong v-if="title" class="alert__title">{{ title }}</strong>
      <div class="alert__content">
        <slot />
      </div>
    </div>
  </div>
</template>

<style scoped>
.alert {
  display: flex;
  gap: var(--space-3);
  padding: var(--space-4);
  border-radius: var(--radius-md);
  border-left: 4px solid var(--color-info);
  background-color: var(--color-info-bg);
  color: var(--color-text);
}

.alert--success {
  border-left-color: var(--color-success);
  background-color: var(--color-success-bg);
}

.alert--warning {
  border-left-color: var(--color-accent);
  background-color: #fffbeb;
}

[data-theme='dark'] .alert--warning {
  background-color: rgb(120 53 15 / 0.15);
}

.alert--error {
  border-left-color: var(--color-error);
  background-color: var(--color-error-bg);
}

.alert__icon {
  flex-shrink: 0;
  color: var(--color-info);
  margin-top: var(--space-1);
}

.alert--success .alert__icon {
  color: var(--color-success);
}

.alert--warning .alert__icon {
  color: var(--color-accent-dark);
}

.alert--error .alert__icon {
  color: var(--color-error);
}

.alert__body {
  flex: 1;
}

.alert__title {
  display: block;
  margin-bottom: var(--space-1);
  font-weight: var(--font-semibold);
}

.alert__content {
  line-height: var(--leading-relaxed);
}

.alert__content :deep(p) {
  margin: 0;
}

.alert__content :deep(a) {
  color: inherit;
  text-decoration: underline;
}

.alert__content :deep(p + p) {
  margin-top: var(--space-2);
}
</style>
